import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Trophy, Target, Lock, Users, BarChart3, Gift, User as UserIcon, Sparkles, ChevronLeft, Shirt } from "lucide-react";

export default function Regeln() {
  useEffect(() => { document.title = "Spielregeln – Englputzeder Tippspiel"; }, []);

  return (
    <div className="space-y-5">
      <Link to="/profil" className="flex items-center gap-1 text-sm text-muted-foreground tap">
        <ChevronLeft className="h-4 w-4" /> Zurück
      </Link>

      <div className="bg-hero text-primary-foreground rounded-lg shadow-pop p-5">
        <div className="text-[10px] font-bold uppercase tracking-wider text-primary-foreground/80">So funktioniert's</div>
        <h1 className="font-display font-extrabold text-3xl mt-1 flex items-center gap-2">
          <Sparkles className="h-7 w-7" /> Spielregeln
        </h1>
        <p className="text-sm text-primary-foreground/90 mt-2">
          Tippe alle Spiele der WM 2026, sammle Punkte und sichere dir einen Englputzeder-Gutschein.
        </p>
      </div>

      <Section icon={<Target className="h-5 w-5" />} title="Punktesystem">
        <ul className="space-y-2 text-sm">
          <Row badge="+4" tone="gold" label="Volltreffer — exaktes Ergebnis getippt" />
          <Row badge="+3" tone="accent" label="Tordifferenz — Differenz & Sieger korrekt" />
          <Row badge="+2" tone="secondary" label="Tendenz — nur Sieger/Unentschieden korrekt" />
          <Row badge="0" tone="muted" label="Falsch getippt" />
        </ul>
      </Section>

      <Section icon={<Gift className="h-5 w-5" />} title="Bonus-Tipps · bis zu +125 Punkte">
        <ul className="space-y-2 text-sm">
          <li className="flex items-center justify-between gap-2"><span>🏆 Weltmeister</span><b className="tabular-nums">+20</b></li>
          <li className="flex items-center justify-between gap-2"><span>⚽ Torschützenkönig</span><b className="tabular-nums">+15</b></li>
          <li className="flex items-center justify-between gap-2"><span>🥇 Gruppensieger A–L (je)</span><b className="tabular-nums">+5</b></li>
          <li className="flex items-center justify-between gap-2"><span>🇦🇹 Österreich-Spezial (6 Fragen, je)</span><b className="tabular-nums">+5</b></li>
        </ul>
        <p className="text-xs text-muted-foreground mt-3">
          Bonus-Tipps gibst du einmal im Onboarding ab — und kannst sie später unter <Link to="/bonus" className="underline">Bonus</Link> ändern, solange das Turnier nicht aufgelöst ist.
        </p>
      </Section>

      <Section icon={<Lock className="h-5 w-5" />} title="Tipp-Sperre">
        <p className="text-sm">
          Tipps für ein Spiel sind <b>15 Minuten vor Anpfiff</b> gesperrt. Danach kannst du dein Ergebnis nicht mehr ändern.
        </p>
        <p className="text-xs text-muted-foreground mt-2">
          Tipp: Stelle alle deine Tipps am besten direkt zu Beginn der WM ein — du kannst sie bis zur Sperre jederzeit anpassen.
        </p>
      </Section>

      <Section icon={<Users className="h-5 w-5" />} title="Tippgruppen">
        <p className="text-sm">
          Tippe gemeinsam mit Freunden, Kollegen oder dem Verein. Jede Gruppe hat einen <b>6-stelligen Code</b> — teile ihn per WhatsApp oder Link.
          In jeder Gruppe gibt es ein <b>eigenes Ranking</b>.
        </p>
        <p className="text-xs text-muted-foreground mt-2">
          🏆 <b>Wichtig:</b> Die kumulierte Punktzahl deiner Gruppe entscheidet über das Englputzeder-Sponsoring (siehe „Preise" unten).
        </p>
        <Link to="/gruppen" className="mt-3 inline-block text-xs font-bold text-accent-foreground bg-accent rounded-pill px-3 py-1.5 tap">
          Zu den Gruppen →
        </Link>
      </Section>

      <Section icon={<BarChart3 className="h-5 w-5" />} title="Tabellen & KO-Phase">
        <p className="text-sm">
          Unter <b>Tabellen</b> siehst du die aktuellen Gruppentabellen (3 Punkte für Sieg, 1 für Unentschieden, Tordifferenz als Tiebreaker)
          sowie die <b>KO-Phase</b> mit Bracket-Visualisierung — wer trifft wo auf wen.
        </p>
        <Link to="/ko-phase" className="mt-3 inline-block text-xs font-bold text-accent-foreground bg-accent rounded-pill px-3 py-1.5 tap">
          Zu den Tabellen →
        </Link>
      </Section>

      <Section icon={<Trophy className="h-5 w-5" />} title="Preise">
        <div className="space-y-3">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-accent mb-1">🏆 Gruppen-Sponsoring</div>
            <p className="text-sm">
              Die <b>3 punktstärksten Tippgruppen</b> gewinnen Englputzeder-Sponsoring (gilt für die Gruppe als Ganzes):
            </p>
            <ul className="mt-2 space-y-1.5 text-sm">
              <Row badge="🥇" tone="gold" label="Platz 1 — 3.000 € Sponsoring" />
              <Row badge="🥈" tone="accent" label="Platz 2 — 1.500 € Sponsoring" />
              <Row badge="🥉" tone="secondary" label="Platz 3 — 500 € Sponsoring" />
            </ul>
            <p className="text-xs text-muted-foreground mt-2">
              Die Gruppen-Punkte sind die <b>Summe</b> der Tipp- und Bonuspunkte aller Mitglieder.
            </p>
          </div>

          <div className="rounded-md border-2 border-accent/40 bg-accent/10 p-3">
            <div className="text-[10px] font-bold uppercase tracking-wider text-accent mb-1 flex items-center gap-1">
              <Shirt className="h-3.5 w-3.5" /> Anmelde-Geschenk
            </div>
            <p className="text-sm font-bold leading-tight">
              🎽 Gratis Englputzeder WM-Trikot + 50 € Gutschein
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Jede:r Neuanmelder:in erhält das Geschenk-Set. Mindestbestellwert für den Gutschein: <b>1.000 €</b>. Trikots solange der Vorrat reicht — also schnell sein!
            </p>
          </div>
        </div>
      </Section>

      <Section icon={<UserIcon className="h-5 w-5" />} title="Profil & Avatar">
        <ul className="text-sm space-y-1.5">
          <li>• Username: 3–20 Zeichen, Buchstaben/Zahlen/Unterstrich</li>
          <li>• Avatar: Emoji oder eigenes Foto (max. 3 MB)</li>
          <li>• Passwort jederzeit im Profil änderbar</li>
        </ul>
      </Section>

      <p className="text-center text-xs text-muted-foreground py-4">
        Fragen? Schreib dem Admin in deiner Gruppe. Viel Erfolg & Spaß beim Tippen! ⚽
      </p>
    </div>
  );
}

function Section({ icon, title, children }: { icon: React.ReactNode; title: string; children: React.ReactNode }) {
  return (
    <section className="bg-card rounded-lg shadow-card p-4">
      <h2 className="font-display font-bold text-lg flex items-center gap-2 mb-3">
        <span className="bg-accent text-accent-foreground rounded-md p-1.5 inline-flex">{icon}</span>
        {title}
      </h2>
      {children}
    </section>
  );
}

function Row({ badge, tone, label }: { badge: string; tone: "gold" | "accent" | "secondary" | "muted"; label: string }) {
  const cls =
    tone === "gold" ? "bg-gold text-foreground" :
    tone === "accent" ? "bg-accent text-accent-foreground" :
    tone === "secondary" ? "bg-secondary text-secondary-foreground" :
    "bg-muted text-muted-foreground";
  return (
    <li className="flex items-center gap-3">
      <span className={`font-display font-extrabold text-sm rounded-pill px-3 py-1 min-w-[44px] text-center tabular-nums ${cls}`}>{badge}</span>
      <span>{label}</span>
    </li>
  );
}
